import { useMemo, createContext, useContext } from "react";
import { useTranslations } from "next-intl";
import { customWidgetCopy } from "@/i18n/widgetCopy";
import { Assistant, PublicAssistant } from "@/generated";
import { ToolDisplayPreset } from "./utils";

export type CitationStyle = "text" | "numbered" | "hidden";

export interface FeedbackOptions {
  placeholder: string;
  submitLabel: string;
  cancelLabel: string;
}

export interface ChatDisplayOptions {
  toolDisplayPreset: ToolDisplayPreset;
  minimalCaretOnly: boolean;
  minimalToolMessage: string;
  citationStyle: CitationStyle;
  deduplicateSourcesByUrl: boolean;
  omitCitationQuotes: boolean;
  showSourceImagesByDefault: boolean;
  hideWorkflowProgress: boolean;
  hideErrorBanners: boolean;
  hideReasoning: boolean;
  groupIntoChainOfThought: boolean;
  isCoworker: boolean;
  isCompact: boolean;
  hideActions: boolean;
  hideRetry?: boolean;
  offerTestThisReply?: boolean;
  rowsShowChanges: boolean;
  showMessageFeedback: boolean;
  messageFooter: string;
  feedback: FeedbackOptions;
}

export const DEFAULT_FEEDBACK_OPTIONS: FeedbackOptions = {
  placeholder: "Add details (optional)",
  submitLabel: "Submit",
  cancelLabel: "Cancel",
};

export const DEFAULT_CHAT_DISPLAY_OPTIONS: ChatDisplayOptions = {
  toolDisplayPreset: "execution",
  minimalCaretOnly: false,
  minimalToolMessage: "Thinking...",
  citationStyle: "text",
  deduplicateSourcesByUrl: false,
  omitCitationQuotes: false,
  showSourceImagesByDefault: false,
  hideWorkflowProgress: false,
  // The fallback for callers that build options themselves (the
  // read-only run viewer). Those are internal surfaces, where the
  // error is the thing you opened the view to see.
  hideErrorBanners: false,
  hideReasoning: false,
  groupIntoChainOfThought: false,
  isCoworker: false,
  isCompact: false,
  hideActions: false,
  offerTestThisReply: false,
  rowsShowChanges: false,
  showMessageFeedback: false,
  messageFooter: "",
  feedback: DEFAULT_FEEDBACK_OPTIONS,
};

export const ChatDisplayOptionsContext = createContext<ChatDisplayOptions>(
  DEFAULT_CHAT_DISPLAY_OPTIONS
);

export function useChatDisplayOptionsContext() {
  return useContext(ChatDisplayOptionsContext);
}

function resolveCitationStyle(
  def: Record<string, unknown> | undefined
): CitationStyle {
  if (!def) return "text";
  // New field takes precedence
  const style = def.citation_style;
  if (style === "text" || style === "numbered" || style === "hidden")
    return style;
  // Backward compat: old boolean field
  if ("show_source_citations" in def) {
    return def.show_source_citations === false ? "hidden" : "text";
  }
  return "text";
}

export interface DisplayOptionOverrides {
  showMessageFeedback?: boolean;
  readOnly?: boolean;
  canResume?: boolean;
  offerTestThisReply?: boolean;
  rowsShowChanges?: boolean;
}

export function useChatDisplayOptions(
  assistantData: Assistant | PublicAssistant | undefined,
  isPublic: boolean,
  isCompact: boolean,
  overrides?: DisplayOptionOverrides
): ChatDisplayOptions {
  const readOnly = !!overrides?.readOnly;
  const hideRetry = readOnly && !overrides?.canResume;
  const offerTestThisReply = !isPublic && !!overrides?.offerTestThisReply;
  const widgetFeedback = !!overrides?.showMessageFeedback;
  const rowsShowChanges = !!overrides?.rowsShowChanges;
  const t = useTranslations("widget");
  return useMemo(() => {
    // For public assistants, display options are top-level fields on PublicAssistant.
    // For private assistants, they live inside the definition dict.
    const def = isPublic
      ? (assistantData as PublicAssistant | undefined)
      : (assistantData as Assistant | undefined)?.definition;

    // Tool display preset with backward compat for legacy hide_tool_io
    let toolDisplayPreset: ToolDisplayPreset = "execution";
    if (def?.tool_display_preset)
      toolDisplayPreset = def.tool_display_preset as ToolDisplayPreset;
    else if (
      !isPublic &&
      (def as Record<string, unknown>)?.hide_tool_io === false
    )
      toolDisplayPreset = "tracing";

    const msg = def?.minimal_tool_message;
    const record = def as Record<string, unknown> | undefined;

    return {
      toolDisplayPreset,
      minimalCaretOnly: !!def?.minimal_caret_only,
      minimalToolMessage:
        customWidgetCopy(msg, "thinking") ?? t("chain.thinking"),
      citationStyle: resolveCitationStyle(record),
      deduplicateSourcesByUrl: !!record?.deduplicate_sources_by_url,
      omitCitationQuotes: !!record?.omit_citation_quotes,
      showSourceImagesByDefault: !!record?.show_source_images,
      hideWorkflowProgress: !!def?.hide_workflow_progress,
      // Absent means an agent saved before the field existed, and the default
      // is to hide — so only an explicit false shows the banner. A falsy
      // coercion would flip every one of those agents the wrong way.
      hideErrorBanners: def?.hide_error_banners !== false,
      hideReasoning: !!def?.hide_reasoning,
      groupIntoChainOfThought: !!record?.group_into_chain_of_thought,
      isCoworker: !isPublic && !!record?.is_coworker,
      isCompact,
      hideActions: false,
      hideRetry,
      offerTestThisReply,
      rowsShowChanges,
      // Feedback is always on in the internal/authenticated chat; the public
      // widget gates it via its deployment config; read-only never shows it.
      showMessageFeedback: readOnly ? false : !isPublic || widgetFeedback,
      messageFooter: resolveMessageFooter(record),
      // Agent definitions persist the English defaults of these labels, so a
      // label still at its default follows the widget language; one the
      // author changed is shown as written.
      feedback: {
        placeholder:
          customWidgetCopy(
            record?.feedback_placeholder,
            "feedbackPlaceholder"
          ) ?? t("feedback.placeholder"),
        submitLabel:
          customWidgetCopy(record?.feedback_submit_label, "feedbackSubmit") ??
          t("feedback.submit"),
        cancelLabel:
          customWidgetCopy(record?.feedback_cancel_label, "feedbackCancel") ??
          t("feedback.cancel"),
      },
    };
  }, [
    assistantData,
    isPublic,
    isCompact,
    readOnly,
    hideRetry,
    offerTestThisReply,
    rowsShowChanges,
    widgetFeedback,
    t,
  ]);
}

// The public widget receives the footer already flattened to a string; the
// authenticated builder gets the raw TipTap document straight off the agent
// definition. Both resolve to the same plain text.
function resolveMessageFooter(
  def: Record<string, unknown> | undefined
): string {
  const raw = def?.message_footer;
  // Public: already gated and flattened by the backend, so there is no
  // enabled flag to consult — a non-empty string means "show it".
  if (typeof raw === "string") return raw.trim();
  if (!def?.message_footer_enabled) return "";
  const doc = Array.isArray(raw) ? raw[0] : raw;
  const text = (doc as { text?: unknown } | null | undefined)?.text;
  return typeof text === "string" ? text.trim() : "";
}
